import React, { FC, useMemo } from 'react';
import { BlockType, BlockWithNotes } from 'src/config/api/models/Block';
import NotesIndicator from '../../../../modules/notes/components/NotesIndicator';
import { useUser } from '../../hooks';
import { Counter, IndicatorsContainer } from './Indicators.styles';
import { useAppSelector } from '../../hooks/useAppDispatch';
import { useParams } from 'react-router';
import { UrlParams } from '../../../shared/types';
import { useStudentFeedbackForTrajectory, useTeacherFeedback } from 'src/modules/feedback/hooks';
import { BookmarkModeEnum, TeacherFiltersEnum } from '../../constants';
import { BookmarkIndicator } from 'src/modules/trajectory/components/bookmark';
import { FeedbackIndicator, FeedbackModule } from 'src/modules/feedback';
import { useDisclosure } from 'react-use-disclosure';
import { Feedback } from 'src/config/api/models/Feedback';
import { SidePanelModes } from '../../slices/sidePanel/sidePanelSlice';
import { ExerciseType, ExerciseWithNotes } from 'src/config/api/models/Exercise';
import {
  useGetStudentBookmarkQuery,
  useGetTeacherBookmarksByBookQuery,
} from '../../slices/bookmark/api/bookmarkApi';
import { getBlockNotes } from '../../slices/notes/api/notes';
import { BlockNote } from 'src/config/api/models/BlockNote';

type IndicatorsProps = {
  pageId?: string | undefined;
  onClickFeedback?: () => void;
  block: BlockWithNotes | ExerciseWithNotes;
  expanded?: string | boolean;
  handleExpand?: (panel?: string) => void;
  leftMargin?: number;
};

export const Indicators: FC<IndicatorsProps> = ({
  pageId,
  onClickFeedback,
  block,
  expanded,
  handleExpand = (id?: string) => {},
  leftMargin = 0,
}) => {
  // hooks
  const { isOpen: feedbackIsOpen, close: closeFeedback } = useDisclosure(false);
  const { isStudent, isTeacher, isAuthor } = useUser();
  const { bookId, sectionCode } = useParams<UrlParams>();
  const { feedback: studentFeedback } = useStudentFeedbackForTrajectory();
  const { feedback: teacherFeedback } = useTeacherFeedback();
  // rtk
  const { data: teacherBookmarks } = useGetTeacherBookmarksByBookQuery(bookId as string, {
    skip: !isTeacher,
  });
  const { data: studentBookmarks } = useGetStudentBookmarkQuery(bookId as string, {
    skip: !isStudent,
  });
  // store
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const sidePanelMode = useAppSelector((state) => state.sidepanel.sidePanelMode);
  const bookmarkMode = useAppSelector((state) => state.bookmark.bookmarkMode);
  const currentBookmarkId = useAppSelector((state) => state.bookmark.currentBookmarkId);

  // feedback
  const relevantTeacherFeedback: Feedback[] | undefined = useMemo(() => {
    if (teacherFeedback != null && teacherFeedback.find((item) => item.pageId === pageId)) {
      return !!!block.id
        ? teacherFeedback.find((item) => item.pageId === pageId)?.trajectorFeedbacks
        : teacherFeedback
            .find((item) => item.pageId === pageId)
            ?.blockFeedbacks?.find((item) => item.blockId === block.id)?.feedbacks;
    }
  }, [teacherFeedback, pageId, block]);

  const relevantStudentFeedback: Feedback[] | undefined = useMemo(() => {
    if (studentFeedback != null && studentFeedback.some((item) => item.pageId === pageId)) {
      return !!!block.id
        ? studentFeedback.find((item) => item.pageId === pageId)?.feedback?.trajectoryFeedbacks
        : studentFeedback
            .find((item) => item.pageId === pageId)
            ?.feedback?.blockFeedbacks.filter((item) => item.blockId === block.id);
    }
  }, [studentFeedback, pageId, block]);

  const hasFeedback = useMemo(() => {
    return !(
      (isTeacher && (relevantTeacherFeedback == null || relevantTeacherFeedback.length === 0)) ||
      (isStudent && (relevantStudentFeedback == null || relevantStudentFeedback.length === 0))
    );
  }, [isStudent, isTeacher, relevantStudentFeedback, relevantTeacherFeedback]);

  const feedbackEnabled = useMemo(
    () =>
      (isTeacher && activeFilter === TeacherFiltersEnum.FOLLOW_UP && selectedGroup !== '') ||
      hasFeedback,
    [isTeacher, activeFilter, selectedGroup, hasFeedback]
  );

  // bookmark
  const bookmarkEnabled = bookmarkMode === BookmarkModeEnum.SELECT_BLOCK;

  const teacherHasBookmark = useMemo(() => {
    if (
      isTeacher &&
      teacherBookmarks &&
      teacherBookmarks.find((bm) => bm.blockId === block.id)?.blockId
    )
      return true;
    return false;
  }, [block.id, isTeacher, teacherBookmarks]);

  const studentHasBookmark = useMemo(() => {
    if (
      isStudent &&
      studentBookmarks &&
      studentBookmarks?.length > 0 &&
      studentBookmarks?.filter((item) => item.blockId === block.id).length
    ) {
      return true;
    }
    return false;
  }, [block.id, isStudent, studentBookmarks]);

  const hasBookmark = (teacherHasBookmark || studentHasBookmark) && block.type !== 'cluster';

  // notes
  const { data: notes } = getBlockNotes({ pageId }, { skip: isAuthor || !pageId });
  const hasNoteAttached = useMemo(
    () =>
      block.note?.id ||
      notes?.find((item: BlockNote) => item.blockId === block.id) ||
      notes?.find((item: BlockNote) => item.blockId === block.parent_id),
    [block.id, block.note?.id, block.parent_id, notes]
  );

  const isEmpty = useMemo(() => {
    if (
      !studentHasBookmark &&
      !teacherHasBookmark &&
      !hasNoteAttached &&
      !feedbackEnabled &&
      !bookmarkEnabled
    )
      return true;
  }, [studentHasBookmark, teacherHasBookmark, hasNoteAttached, feedbackEnabled, bookmarkEnabled]);

  const indicatorConfig = useMemo(() => {
    if (
      [hasNoteAttached, hasBookmark, hasFeedback].filter(Boolean).length > 1 &&
      bookmarkMode === BookmarkModeEnum.DISABLED &&
      (sidePanelMode === undefined ||
        sidePanelMode === 'none' ||
        sidePanelMode !== SidePanelModes.NOTES) &&
      block.type !== 'cluster'
    ) {
      return {
        active: true,
        component: (
          <>
            {expanded ? (
              <>
                {hasBookmark && <BookmarkIndicator blockId={block?.id} />}
                {(feedbackEnabled || hasFeedback) && (
                  <FeedbackIndicator
                    pageId={pageId as string}
                    blockId={block.id}
                    blockType={block.type as BlockType}
                    onClick={onClickFeedback as any}
                    hasFeedback={hasFeedback}
                  />
                )}
                {hasNoteAttached && (
                  <NotesIndicator
                    block={block}
                    note={block.note}
                    feedbackEnabled={feedbackEnabled}
                    bookmarkEnabled={bookmarkEnabled}
                    pageId={pageId as string}
                  />
                )}
              </>
            ) : (
              <Counter onClick={() => handleExpand(block.id)}>
                {[hasNoteAttached, hasBookmark, hasFeedback].filter(Boolean).length}
              </Counter>
            )}
          </>
        ),
      };
    }
    // condition: notes mode is active
    if (
      (hasNoteAttached || sidePanelMode === SidePanelModes.NOTES) &&
      bookmarkMode === BookmarkModeEnum.DISABLED
    ) {
      return {
        active: hasNoteAttached,
        component: (
          <NotesIndicator
            block={block}
            note={block.note}
            feedbackEnabled={feedbackEnabled}
            bookmarkEnabled={bookmarkEnabled}
            pageId={pageId as string}
          />
        ),
      };
    }
    // condition: bookmark mode is active
    if (
      (hasBookmark ||
        bookmarkMode === BookmarkModeEnum.SELECT_BLOCK ||
        bookmarkMode === BookmarkModeEnum.TEACHER_OPTIONS) &&
      sidePanelMode !== SidePanelModes.NOTES &&
      block.type !== 'cluster'
    ) {
      return {
        active: hasBookmark,
        component: <BookmarkIndicator blockId={block?.id} />,
      };
    }
    // condition: feedback is active
    if (feedbackEnabled) {
      return {
        active: hasFeedback,
        component: (
          <FeedbackIndicator
            pageId={pageId as string}
            blockId={block.id}
            blockType={block.type as BlockType}
            onClick={onClickFeedback as any}
            hasFeedback={hasFeedback}
          />
        ),
      };
    }
    return undefined;
  }, [
    block,
    bookmarkEnabled,
    bookmarkMode,
    expanded,
    feedbackEnabled,
    handleExpand,
    hasBookmark,
    hasFeedback,
    hasNoteAttached,
    onClickFeedback,
    pageId,
    sidePanelMode,
  ]);

  if (
    isEmpty &&
    [
      SidePanelModes.NONE,
      undefined,
      SidePanelModes.GROUP_NOTES,
      SidePanelModes.DIFFERENTIATION,
    ].includes(sidePanelMode)
  ) {
    return null;
  }

  const isbookmarkToEdit = bookmarkMode === 'teacherOptions' && currentBookmarkId === block.id;

  if (!isStudent && !isTeacher) return null;

  return (
    <>
      {[BlockType.WYSIWYG, BlockType.STUDY_GUIDE, BlockType.H5P, ExerciseType.H5P].includes(
        block?.type
      ) && (
        <FeedbackModule
          pageId={pageId as string}
          blockId={block.id}
          context="block"
          mode="floating"
          open={feedbackIsOpen}
          onClose={closeFeedback}
        />
      )}
      {indicatorConfig && (
        <IndicatorsContainer
          leftMargin={leftMargin}
          id={`indicator_block_${block.id}`}
          className={`content ${indicatorConfig?.active ? 'active' : ''} ${
            sectionCode === '2' ? 'exercises' : ''
          } `}
        >
          <div
            className={`content ${indicatorConfig?.active ? 'active' : ''} ${
              sectionCode === '2' ? 'exercises' : ''
            } ${isbookmarkToEdit ? 'selected' : ''}`}
          >
            {indicatorConfig?.component}
          </div>
        </IndicatorsContainer>
      )}
    </>
  );
};
